import { useEffect, useState } from 'react';

const HINT_KEY = 'performance-pane-hint-shown';

// Full-height, text-only handle strip on the right viewport edge: the label
// in vertical type between two hairline rules, in Predico teal (brand-600,
// the logo color). The whole 64px gutter (matching the lg:px-16 page padding,
// kept on 2xl too; see Layout.js)
// is clickable. Hovering anywhere on the strip deepens the ink and stretches
// the flanking rules: a quiet "this whole edge is a control" cue.
//
// Desktop-only (lg+): below lg the px-16 gutter it lives in does not exist,
// so the strip would overlap page content; the Dashboard renders a regular
// top-of-page button instead.
//
// Discoverability cue: once per login a light gleam sweeps down the label
// (slide-to-unlock recipe) in periodic single pulses, then the handle stays
// quiet. The login thunk clears the sessionStorage flag so each sign-in
// replays it exactly once.
const PerformancePaneHandle = ({ onOpen }) => {
  const [hinting, setHinting] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(HINT_KEY)) return undefined;
    sessionStorage.setItem(HINT_KEY, '1');
    setHinting(true);
    // 1s delay + 4 x 3.5s cycles (one sweep + rest each), then drop the class.
    const timer = setTimeout(() => setHinting(false), 15200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open cross-market performance"
      className="group fixed inset-y-0 right-0 z-40 hidden lg:flex w-16 flex-col items-center justify-center gap-3 text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
    >
      {/* Hover veil, transparent at the content side, faint teal at the
          viewport edge. Gradients cannot transition, so fade the overlay. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent from-60% to-slate-500/[0.09] dark:to-slate-400/[0.09] opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      />
      <span
        aria-hidden="true"
        className="w-px h-10 bg-current opacity-40 transition-all duration-300 group-hover:h-20 group-hover:opacity-80"
      />
      <span
        className={`relative [writing-mode:vertical-rl] text-[13px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap ${hinting ? 'pane-hint-shimmer' : ''}`}
      >
        Cross-market performance
      </span>
      <span
        aria-hidden="true"
        className="w-px h-10 bg-current opacity-40 transition-all duration-300 group-hover:h-20 group-hover:opacity-80"
      />
    </button>
  );
};

export default PerformancePaneHandle;
